import { create } from 'zustand';
import { Tag } from '../types';
import { supabase } from '../config/supabase';

interface TagState {
  tags: Tag[];
  isLoading: boolean;
  error: string | null;
  lastFetch: number;
  selectedTag: Tag | null;
  
  // Actions
  fetchTags: () => Promise<void>;
  createTag: (name: string, color: string) => Promise<Tag>;
  updateTag: (id: string, updates: Partial<Tag>) => Promise<void>;
  deleteTag: (id: string) => Promise<void>;
  selectTag: (tag: Tag | null) => void;
}

export const useTagStore = create<TagState>((set, get) => ({
  tags: [],
  isLoading: false,
  error: null,
  lastFetch: 0,
  selectedTag: null,

  fetchTags: async () => {
    // Check if we need to fetch - do this BEFORE setting isLoading
    const now = Date.now();
    const state = get();
    if (state.tags.length > 0 && now - state.lastFetch < 300000) { // 5 minutes cache
      return; // Use cached data, no loading state
    }
    
    try {
      // Only show loading if we don't have cached data
      if (state.tags.length === 0) {
        set({ isLoading: true });
      }
      
      const { data: tags, error } = await supabase
        .from('tags')
        .select(`
          *,
          usage:tag_usage_stats(
            file_count,
            last_used
          )
        `)
        .order('name');

      if (error) throw error;

      set({ 
        tags: tags || [],
        lastFetch: now,
        isLoading: false
      });
    } catch (error) {
      console.error('Error fetching tags:', error);
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  createTag: async (name: string, color: string) => {
    try {
      const { data, error } = await supabase
        .from('tags')
        .insert({ name, color })
        .select()
        .single();

      if (error) throw error;

      // Force refresh after creating new tag
      set({ lastFetch: 0 });
      set((state) => ({
        tags: [...state.tags, data]
      }));

      return data;
    } catch (error) {
      console.error('Error creating tag:', error);
      throw error;
    }
  },

  updateTag: async (id: string, updates: Partial<Tag>) => {
    try {
      const { error } = await supabase
        .from('tags')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      // Force refresh after update
      set({ lastFetch: 0 });
      set((state) => ({
        tags: state.tags.map(tag => 
          tag.id === id ? { ...tag, ...updates } : tag
        ),
        selectedTag: state.selectedTag?.id === id 
          ? { ...state.selectedTag, ...updates }
          : state.selectedTag
      }));
    } catch (error) {
      console.error('Error updating tag:', error);
      throw error;
    }
  },

  deleteTag: async (id: string) => {
    try {
      const { error } = await supabase
        .from('tags')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Force refresh after deletion
      set({ lastFetch: 0 });
      set((state) => ({
        tags: state.tags.filter(tag => tag.id !== id),
        selectedTag: state.selectedTag?.id === id ? null : state.selectedTag
      }));
    } catch (error) {
      console.error('Error deleting tag:', error);
      throw error;
    }
  },

  selectTag: (tag) => set({ selectedTag: tag })
}));